export default function Solutions() {
  const steps = [
    {
      number: '01',
      title: 'Compliance Assessment',
      description:
        'Comprehensive evaluation of your current cybersecurity posture against SB 2610 requirements for law firms.',
      features: [
        'Gap analysis of existing security measures',
        'Practice management system review',
        'Attorney-client privilege protection audit',
        'Detailed compliance roadmap',
      ],
    },
    {
      number: '02',
      title: 'Implementation',
      description:
        'Deploy required security controls and technologies tailored specifically for legal practice environments.',
      features: [
        'Zero-Trust security architecture',
        'Encrypted communication systems',
        'Secure document management',
        'Access control implementation',
      ],
    },
    {
      number: '03',
      title: 'Documentation',
      description:
        'Create and maintain all required compliance documentation for state audits and professional responsibility.',
      features: [
        'Security policy documentation',
        'Incident response procedures',
        'Risk assessment reports',
        'Training completion records',
      ],
    },
    {
      number: '04',
      title: 'Ongoing Monitoring',
      description:
        '24/7 security monitoring and continuous compliance management to maintain SB 2610 adherence.',
      features: [
        'Real-time threat detection',
        'Quarterly compliance reviews',
        'Annual risk assessments',
        'Regular staff training updates',
      ],
    },
  ];

  return (
    <section id="solutions" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-4">
            <i className="ri-lightbulb-line text-[#e10708] text-xl mr-2 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-[#e10708] text-sm uppercase tracking-widest font-light">
              Our Solutions
            </span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-serif text-[#1A1F2E] leading-tight mb-6">
            Complete SB 2610<br />
            Compliance Services
          </h2>

          <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
            We provide end-to-end compliance solutions designed specifically for Texas law firms, ensuring full adherence to SB 2610 requirements while protecting attorney-client privilege.
          </p>
        </div>

        {/* Process Steps */}
        <div className="space-y-8 mb-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className="bg-gradient-to-r from-gray-50 to-white rounded-2xl p-8 lg:p-10 border border-gray-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* Step Number */}
                <div className="lg:col-span-2">
                  <div className="text-6xl font-bold text-[#e10708]/20">
                    {step.number}
                  </div>
                </div>

                {/* Content */}
                <div className="lg:col-span-10">
                  <h3 className="text-2xl font-bold text-[#1A1F2E] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">
                    {step.description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {step.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <i className="ri-check-double-line text-[#e10708] text-xl mr-3 mt-0.5 flex-shrink-0 w-5 h-5 flex items-center justify-center"></i>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Service Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-[#FAF9F6] rounded-xl p-8 border border-[#e10708]/20">
            <div className="w-14 h-14 bg-[#e10708]/10 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-shield-check-line text-[#e10708] text-2xl w-6 h-6 flex items-center justify-center"></i>
            </div>
            <h3 className="text-xl font-bold text-[#1A1F2E] mb-3">
              100% Compliance Rate
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Every law firm we've partnered with has achieved and maintained full SB 2610 compliance with zero violations.
            </p>
          </div>

          <div className="bg-[#FAF9F6] rounded-xl p-8 border border-[#e10708]/20">
            <div className="w-14 h-14 bg-[#e10708]/10 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-time-line text-[#e10708] text-2xl w-6 h-6 flex items-center justify-center"></i>
            </div>
            <h3 className="text-xl font-bold text-[#1A1F2E] mb-3">
              24/7 Monitoring
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Round-the-clock security monitoring and incident response to protect your firm and client data at all times.
            </p>
          </div>

          <div className="bg-[#FAF9F6] rounded-xl p-8 border border-[#e10708]/20">
            <div className="w-14 h-14 bg-[#e10708]/10 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-team-line text-[#e10708] text-2xl w-6 h-6 flex items-center justify-center"></i>
            </div>
            <h3 className="text-xl font-bold text-[#1A1F2E] mb-3">
              500+ Law Firms
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Trusted by over 500 Texas law firms for comprehensive IT services and cybersecurity compliance solutions.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}